(() => {
  if (customElements.get('live-viewer-count')) return;

  class LiveViewerCount extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      const configuredMin = this.readNumber('min', 21);
      const configuredMax = this.readNumber('max', 150);
      const configuredStepMin = this.readNumber('stepMin', 1);
      const configuredStepMax = this.readNumber('stepMax', 3);
      const configuredDelayMin = this.readNumber('delayMin', 3000);
      const configuredDelayMax = this.readNumber('delayMax', 5000);

      this.config = {
        min: Math.min(configuredMin, configuredMax),
        max: Math.max(configuredMin, configuredMax),
        stepMin: Math.min(configuredStepMin, configuredStepMax),
        stepMax: Math.max(configuredStepMin, configuredStepMax),
        delayMin: Math.min(configuredDelayMin, configuredDelayMax),
        delayMax: Math.max(configuredDelayMin, configuredDelayMax),
        holdChance: Math.min(100, Math.max(0, this.readNumber('holdChance', 0))),
        persist: this.dataset.persist === 'true',
        ripple: this.dataset.ripple === 'true'
      };

      this.numberElements = Array.from(this.querySelectorAll('[data-live-viewer-number]'));
      this.viewport = this.querySelector('[data-live-viewer-viewport]');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.highlightLayer = this.ensureHighlightLayer();
      this.storageKey = this.dataset.storageKey || '';
      this.current = this.restoreCount();

      if (!Number.isFinite(this.current)) {
        this.current = this.randomInteger(this.config.min, this.config.max);
      }

      this.current = this.clamp(this.current);
      this.render(false);

      if (this.config.ripple) {
        this.introTimer = window.setTimeout(() => this.playTransition(), 450);
      }

      this.handleVisibilityChange = this.onVisibilityChange.bind(this);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      this.scheduleNextUpdate();
    }

    disconnectedCallback() {
      window.clearTimeout(this.updateTimer);
      window.clearTimeout(this.rippleTimer);
      window.clearTimeout(this.numberTimer);
      window.clearTimeout(this.introTimer);
      if (this.highlightAnimation) this.highlightAnimation.cancel();
      if (this.numberAnimations) this.numberAnimations.forEach((animation) => animation.cancel());
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.dataset.initialized = 'false';
    }

    readNumber(key, fallback) {
      const value = Number(this.dataset[key]);
      return Number.isFinite(value) ? value : fallback;
    }

    randomInteger(min, max) {
      return Math.floor(Math.random() * (max - min + 1)) + min;
    }

    clamp(value) {
      return Math.min(this.config.max, Math.max(this.config.min, Math.round(value)));
    }

    restoreCount() {
      if (!this.config.persist || !this.storageKey) return Number.NaN;

      try {
        const storedValue = Number(window.sessionStorage.getItem(this.storageKey));
        if (Number.isFinite(storedValue)) return storedValue;
      } catch (error) {
        return Number.NaN;
      }

      return Number.NaN;
    }

    persistCount() {
      if (!this.config.persist || !this.storageKey) return;

      try {
        window.sessionStorage.setItem(this.storageKey, String(this.current));
      } catch (error) {
        // Storage can be unavailable in privacy modes; the counter still works in memory.
      }
    }

    ensureHighlightLayer() {
      let layer = this.querySelector('[data-live-viewer-highlight]');
      if (layer) return layer;

      layer = document.createElement('span');
      layer.setAttribute('data-live-viewer-highlight', '');
      layer.setAttribute('aria-hidden', 'true');
      layer.style.cssText = [
        'position:absolute',
        'z-index:1',
        'inset:0',
        'display:block',
        'width:100%',
        'height:100%',
        'pointer-events:none',
        'opacity:0',
        'background:linear-gradient(90deg,rgba(255,106,0,.12) 0%,rgba(255,255,255,.92) 55%,rgba(255,106,0,.16) 100%)',
        'clip-path:inset(0 100% 0 0)',
        'will-change:clip-path,opacity'
      ].join(';');

      this.style.position = 'relative';
      this.style.overflow = 'hidden';
      this.style.isolation = 'isolate';
      if (this.viewport) {
        this.viewport.style.position = 'relative';
        this.viewport.style.zIndex = '2';
      }
      this.insertBefore(layer, this.firstChild);
      return layer;
    }

    getWeightedStep() {
      const range = this.config.stepMax - this.config.stepMin + 1;
      const weightedRandom = Math.pow(Math.random(), 1.7);
      return this.config.stepMin + Math.min(range - 1, Math.floor(weightedRandom * range));
    }

    getNextCount() {
      if (Math.random() * 100 < this.config.holdChance) return this.current;
      if (this.config.max === this.config.min) return this.current;

      const position = (this.current - this.config.min) / (this.config.max - this.config.min);
      const upwardChance = 0.72 - position * 0.44;
      const direction = Math.random() < upwardChance ? 1 : -1;
      const step = this.getWeightedStep();
      let next = this.clamp(this.current + direction * step);

      if (next === this.current) {
        next = this.clamp(this.current - direction * step);
      }

      return next;
    }

    render(animate) {
      this.numberElements.forEach((element) => {
        element.textContent = String(this.current);
      });

      this.setAttribute('data-current-count', String(this.current));
      this.persistCount();

      if (animate && this.config.ripple) this.playTransition();
    }

    playTransition() {
      if (this.reducedMotionQuery.matches) return;

      if (this.highlightLayer && typeof this.highlightLayer.animate === 'function') {
        if (this.highlightAnimation) this.highlightAnimation.cancel();
        if (this.numberAnimations) this.numberAnimations.forEach((animation) => animation.cancel());

        this.classList.remove('is-rippling');
        this.classList.remove('is-count-changing');

        this.highlightAnimation = this.highlightLayer.animate(
          [
            { clipPath: 'inset(0 100% 0 0)', opacity: 1 },
            { clipPath: 'inset(0 0% 0 0)', opacity: 0.82, offset: 0.68 },
            { clipPath: 'inset(0 0% 0 0)', opacity: 0 }
          ],
          { duration: 2000, easing: 'ease-out', fill: 'both' }
        );

        this.numberAnimations = this.numberElements.map((element) =>
          element.animate(
            [
              { opacity: 0, transform: 'translateY(5px) scale(.92)' },
              { opacity: 1, transform: 'translateY(-1px) scale(1.06)', offset: 0.65 },
              { opacity: 1, transform: 'translateY(0) scale(1)' }
            ],
            { duration: 480, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'both' }
          )
        );
        return;
      }

      this.classList.remove('is-rippling');
      this.classList.remove('is-count-changing');
      void this.offsetWidth;
      this.classList.add('is-rippling');
      this.classList.add('is-count-changing');

      window.clearTimeout(this.rippleTimer);
      window.clearTimeout(this.numberTimer);

      this.numberTimer = window.setTimeout(() => {
        this.classList.remove('is-count-changing');
      }, 480);

      this.rippleTimer = window.setTimeout(() => {
        this.classList.remove('is-rippling');
      }, 2000);
    }

    updateCount() {
      const next = this.getNextCount();
      if (next !== this.current) {
        this.current = next;
        this.render(true);
      }
      this.scheduleNextUpdate();
    }

    scheduleNextUpdate() {
      window.clearTimeout(this.updateTimer);
      if (document.hidden) return;

      const delay = this.randomInteger(this.config.delayMin, this.config.delayMax);
      this.updateTimer = window.setTimeout(() => this.updateCount(), delay);
    }

    onVisibilityChange() {
      if (document.hidden) {
        window.clearTimeout(this.updateTimer);
      } else {
        this.scheduleNextUpdate();
      }
    }
  }

  customElements.define('live-viewer-count', LiveViewerCount);
})();
